import { useEffect, useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import PublicLayout from './PublicLayout';
import LanguageMeta from './LanguageMeta';
import PublicSeoDefaults from './public/PublicSeoDefaults';
import { SeoOwnershipProvider } from './public/SeoOwnership';
import { trackVisit, trackIntent, intentTarget } from '../lib/acquisitionJourney';

/**
 * Wraps PublicLayout for language-prefixed public routes (/fr/*, /en/*).
 * Extracts the language from the first URL segment and syncs it to I18nContext.
 */
export default function LocalizedPublicLayout() {
  const { lang: ctxLang, setLang } = useI18n();
  const { pathname } = useLocation();

  const urlLang = useMemo(() => {
    const seg = pathname.split('/')[1];
    return seg === 'fr' || seg === 'en' ? seg : null;
  }, [pathname]);

  // Sync URL language to context (always called, regardless of urlLang validity)
  useEffect(() => {
    if (urlLang && urlLang !== ctxLang) {
      setLang(urlLang);
    }
  }, [urlLang, ctxLang, setLang]);

  // ACQUISITION V2 — parcours organique anonyme : visite (une fois/session)
  // + intention commerciale (clic méthode/implantation/réalisation), mesurés
  // sur les éléments EXISTANTS — aucun bloc visible ajouté (doctrine PO).
  useEffect(() => {
    trackVisit(window.location.pathname);
    const onClick = (e) => {
      try {
        const a = e.target.closest && e.target.closest('a[href]');
        if (!a) return;
        const target = intentTarget(a.getAttribute('href'));
        if (target) trackIntent(target);
      } catch { /* mesure silencieuse */ }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // Redirect unknown language prefixes to French default (after all hooks)
  if (!urlLang) {
    return <Navigate to="/fr" replace />;
  }

  return (
    <SeoOwnershipProvider>
      <LanguageMeta />
      <PublicSeoDefaults />
      <PublicLayout />
    </SeoOwnershipProvider>
  );
}
